/* eslint-disable @typescript-eslint/no-explicit-any */
import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
} from '@angular/forms';
import {buildPaginatedListFilters} from '@gatewee/services/filter-query-params.util';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiDropdown, TuiTextfield} from '@taiga-ui/core';
import {TuiChevron} from '@taiga-ui/kit';
import {Observable, Subject, of} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';

const tuiDropdownDirectives = [...TuiDropdown] as const;

/** Option emitted by `gatewee-select-async`. */
export interface AsyncOption<V = unknown, M = unknown> {
  value: V;
  label: string;
  meta?: M;
}

interface AsyncFetchParams {
  search: string;
  skip: number;
  take: number;
}

interface AsyncFetchResult<V, M> {
  items: AsyncOption<V, M>[];
  total: number;
}

interface AsyncCrudSource<T = unknown> {
  findAllPaginated(
    skip: number,
    take: number,
    filters?: Record<string, unknown>,
    skipSpinner?: boolean,
  ): Observable<{records: T[]; count: number}>;
  findOne(
    id: number,
    filters?: Record<string, unknown>,
    skipSpinner?: boolean,
  ): Observable<T>;
}

function createAsyncCrudFetchers<T, V = number, M = T>(config: {
  source: AsyncCrudSource<T>;
  toOption: (entity: T) => AsyncOption<V, M>;
  listOptions?: Record<string, unknown>;
  itemFilter?: (entity: T) => boolean;
  idFromValue?: (value: V) => number;
  skipSpinner?: boolean;
}): {
  fetchPage: (params: AsyncFetchParams) => Observable<AsyncFetchResult<V, M>>;
  fetchByValue: (value: V) => Observable<AsyncOption<V, M>>;
} {
  const {
    source,
    toOption,
    listOptions,
    itemFilter,
    idFromValue,
    skipSpinner = true,
  } = config;

  const fetchPage = (
    params: AsyncFetchParams,
  ): Observable<AsyncFetchResult<V, M>> =>
    source
      .findAllPaginated(
        params.skip,
        params.take,
        buildPaginatedListFilters(listOptions, params.search),
        skipSpinner,
      )
      .pipe(
        map(res => {
          const records = itemFilter
            ? res.records.filter(itemFilter)
            : res.records;
          const items = records.map(toOption);
          const pageFull = res.records.length === params.take;
          const total = pageFull
            ? params.skip + items.length + 1
            : params.skip + items.length;
          return {items, total};
        }),
      );

  const fetchByValue = (value: V): Observable<AsyncOption<V, M>> => {
    const id = idFromValue ? idFromValue(value) : (value as unknown as number);
    const detailFilters = listOptions
      ? buildPaginatedListFilters(listOptions)
      : undefined;
    return source.findOne(id, detailFilters, skipSpinner).pipe(map(toOption));
  };

  return {fetchPage, fetchByValue};
}

type AsyncState<V, M> = {
  items: AsyncOption<V, M>[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
};

const scrollThresholdPx = 64;
const debounceMs = 500;

@Component({
  selector: 'gatewee-select-async',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TuiTextfield,
    TuiChevron,
    ...tuiDropdownDirectives,
    ErrorMessageComponent,
  ],
  templateUrl: './gatewee-select-async.component.html',
  styleUrls: ['./gatewee-select-async.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GateweeSelectAsyncComponent<V = unknown, M = unknown>
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() source?: AsyncCrudSource<any>;
  @Input() toOption?: (entity: any) => AsyncOption<V, M>;
  @Input() listOptions?: Record<string, unknown>;
  @Input() itemFilter?: (entity: any) => boolean;
  @Input() idFromValue?: (value: V) => number;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() required = false;
  @Input() class = '';
  @Input() pageSize = 44;
  @Input() minSearchLength = 0;
  @Input() skipSpinner = true;

  @Output() selectionChange = new EventEmitter<AsyncOption<V, M> | null>();

  @ViewChild('listRef') listRef?: ElementRef<HTMLElement>;

  open = false;
  disabled = false;
  searchTerm = '';
  activeIndex = -1;
  selectedOption: AsyncOption<V, M> | null = null;
  state: AsyncState<V, M> = {
    items: [],
    total: 0,
    loading: false,
    loadingMore: false,
    error: false,
  };

  private value: V | null = null;
  private onChange: (v: V | null) => void = () => {};
  private onTouched: () => void = () => {};

  private fetchPage?: (
    params: AsyncFetchParams,
  ) => Observable<AsyncFetchResult<V, M>>;
  private fetchByValue?: (value: V) => Observable<AsyncOption<V, M>>;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private resolvingValue: V | null = null;
  private skipNextAutoOpen = false;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private readonly cd: ChangeDetectorRef,
    private readonly translate: TranslateService,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control(): FormControl | null {
    return (this.ngControl?.control as FormControl) ?? null;
  }

  get effectiveInputId(): string {
    const name = this.ngControl?.name;
    if (name !== null && name !== undefined && String(name).trim() !== '') {
      return `gatewee-select-async-${String(name)}`;
    }
    return 'gatewee-select-async';
  }

  get hasMore(): boolean {
    return this.state.items.length < this.state.total;
  }

  get listboxId(): string {
    return `${this.effectiveInputId}-listbox`;
  }

  get activeOptionId(): string | null {
    if (this.activeIndex < 0) {
      return null;
    }
    return this.optionId(this.activeIndex);
  }

  optionId(index: number): string {
    return `${this.listboxId}-opt-${index}`;
  }

  ngOnInit(): void {
    if (this.source && this.toOption) {
      const built = createAsyncCrudFetchers<unknown, V, M>({
        source: this.source,
        toOption: this.toOption,
        listOptions: this.listOptions,
        itemFilter: this.itemFilter,
        idFromValue: this.idFromValue,
        skipSpinner: this.skipSpinner,
      });
      this.fetchPage = built.fetchPage;
      this.fetchByValue = built.fetchByValue;
    }

    this.search$
      .pipe(
        debounceTime(debounceMs),
        distinctUntilChanged(),
        tap(() => {
          this.state = {
            ...this.state,
            loading: this.state.items.length === 0,
            loadingMore: false,
            error: false,
          };
          if (!this.disabled && !this.skipNextAutoOpen) {
            this.open = true;
          }
          this.cd.markForCheck();
        }),
        switchMap(term => {
          if (term.length < this.minSearchLength || !this.fetchPage) {
            return of<AsyncFetchResult<V, M>>({items: [], total: 0});
          }
          return this.fetchPage({
            search: term,
            skip: 0,
            take: this.pageSize,
          }).pipe(
            catchError(() => {
              this.state = {...this.state, error: true};
              return of<AsyncFetchResult<V, M>>({items: [], total: 0});
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(result => {
        this.state = {
          items: result.items,
          total: result.total,
          loading: false,
          loadingMore: false,
          error: this.state.error,
        };
        if (!this.skipNextAutoOpen && !this.disabled) {
          this.open = true;
        }
        this.resetActiveIndexAfterSearch();
        this.skipNextAutoOpen = false;
        this.cd.markForCheck();
      });

    this.skipNextAutoOpen = true;
    this.search$.next('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(v: V | null): void {
    this.value = v;
    if (v === null || v === undefined) {
      this.selectedOption = null;
      this.searchTerm = '';
      this.cd.markForCheck();
      return;
    }
    const found = this.state.items.find(i => this.equals(i.value, v));
    if (found) {
      this.selectedOption = found;
      this.searchTerm = found.label;
      this.cd.markForCheck();
      return;
    }
    if (this.fetchByValue && this.resolvingValue !== v) {
      this.resolvingValue = v;
      this.fetchByValue(v)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: opt => {
            if (this.equals(this.value, v)) {
              this.selectedOption = opt;
              this.searchTerm = opt.label;
              this.selectionChange.emit(opt);
            }
            this.resolvingValue = null;
            this.cd.markForCheck();
          },
          error: () => {
            this.resolvingValue = null;
          },
        });
    }
    this.cd.markForCheck();
  }

  registerOnChange(fn: (v: V | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    if (!this.open) {
      this.open = true;
    }
    this.search$.next(value);
  }

  onFocus(event: FocusEvent): void {
    if (this.disabled || this.open) {
      return;
    }
    this.open = true;
    this.syncActiveIndexToSelection();
    this.search$.next('');
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      setTimeout(() => target.select(), 0);
    }
    this.cd.markForCheck();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          this.search$.next(this.searchTerm);
          return;
        }
        if (!this.state.items.length) {
          return;
        }
        if (this.activeIndex < 0) {
          this.setActive(0);
        } else {
          const next = Math.min(
            this.state.items.length - 1,
            this.activeIndex + 1,
          );
          this.setActive(next);
        }
        this.prefetchIfNearEnd();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.open || !this.state.items.length) {
          return;
        }
        if (this.activeIndex < 0) {
          this.setActive(this.state.items.length - 1);
        } else {
          this.setActive(Math.max(0, this.activeIndex - 1));
        }
        break;
      case 'Home':
        if (this.open && this.state.items.length) {
          event.preventDefault();
          this.setActive(0);
        }
        break;
      case 'End':
        if (this.open && this.state.items.length) {
          event.preventDefault();
          this.setActive(this.state.items.length - 1);
          this.prefetchIfNearEnd();
        }
        break;
      case 'Enter':
        if (
          this.open &&
          this.activeIndex >= 0 &&
          this.activeIndex < this.state.items.length
        ) {
          event.preventDefault();
          this.selectItem(this.state.items[this.activeIndex]);
        }
        break;
      case 'Escape':
        if (this.open) {
          event.preventDefault();
          event.stopPropagation();
          this.closeDropdown();
        }
        break;
      case 'Tab':
        if (this.open) {
          this.closeDropdown();
        }
        break;
    }
  }

  onOptionMouseEnter(index: number): void {
    if (this.activeIndex !== index) {
      this.activeIndex = index;
      this.cd.markForCheck();
    }
  }

  isOptionSelected(item: AsyncOption<V, M>): boolean {
    if (!this.selectedOption) {
      return false;
    }
    return this.equals(this.selectedOption.value, item.value);
  }

  onActiveZoneChange(active: boolean): void {
    if (active) {
      return;
    }
    void Promise.resolve().then(() => {
      if (this.isInputFocused()) {
        return;
      }
      if (this.state.loading || this.state.loadingMore) {
        return;
      }
      this.closeDropdown();
      this.onTouched();
      this.control?.markAsTouched();
      this.cd.markForCheck();
    });
  }

  selectItem(item: AsyncOption<V, M>): void {
    this.selectedOption = item;
    this.value = item.value;
    this.searchTerm = item.label;
    this.closeDropdown();
    this.onChange(item.value);
    this.selectionChange.emit(item);
    this.onTouched();
    this.control?.markAsTouched();
    this.cd.markForCheck();
  }

  clearValue(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.selectedOption = null;
    this.value = null;
    this.searchTerm = '';
    this.onChange(null);
    this.selectionChange.emit(null);
    this.search$.next('');
    this.cd.markForCheck();
  }

  onListScroll(): void {
    const el = this.listRef?.nativeElement;
    if (!el) {
      return;
    }
    if (
      this.state.loading ||
      this.state.loadingMore ||
      this.state.error ||
      !this.hasMore
    ) {
      return;
    }
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= scrollThresholdPx) {
      this.loadMore();
    }
  }

  retry(): void {
    this.state = {...this.state, error: false};
    this.search$.next(this.searchTerm);
  }

  trackByValue = (_: number, item: AsyncOption<V, M>): unknown => item.value;

  t(key: string): string {
    const value = this.translate.instant(key);
    return typeof value === 'string' ? value : key;
  }

  private setActive(index: number): void {
    if (index < 0 || index >= this.state.items.length) {
      return;
    }
    this.activeIndex = index;
    this.cd.markForCheck();
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    if (this.activeIndex < 0) {
      return;
    }
    void Promise.resolve().then(() => {
      const el = document.getElementById(this.optionId(this.activeIndex));
      el?.scrollIntoView({block: 'nearest'});
    });
  }

  private prefetchIfNearEnd(): void {
    if (
      this.activeIndex < 0 ||
      this.activeIndex < this.state.items.length - 5 ||
      !this.hasMore ||
      this.state.loading ||
      this.state.loadingMore ||
      this.state.error
    ) {
      return;
    }
    this.loadMore();
  }

  private resetActiveIndexAfterSearch(): void {
    this.activeIndex = this.state.items.length > 0 ? 0 : -1;
  }

  private syncActiveIndexToSelection(): void {
    if (!this.selectedOption || !this.state.items.length) {
      this.activeIndex = -1;
      return;
    }
    const index = this.state.items.findIndex(item =>
      this.equals(item.value, this.selectedOption?.value),
    );
    this.activeIndex = index >= 0 ? index : -1;
  }

  private closeDropdown(): void {
    this.open = false;
    this.activeIndex = -1;
    const expectedLabel = this.selectedOption?.label ?? '';
    if (this.searchTerm !== expectedLabel) {
      this.searchTerm = expectedLabel;
    }
    this.cd.markForCheck();
  }

  private loadMore(): void {
    if (!this.fetchPage) {
      return;
    }
    this.state = {...this.state, loadingMore: true};
    if (!this.disabled) {
      this.open = true;
    }
    this.cd.markForCheck();
    this.fetchPage({
      search: this.searchTerm,
      skip: this.state.items.length,
      take: this.pageSize,
    })
      .pipe(
        catchError(() => {
          this.state = {...this.state, error: true, loadingMore: false};
          this.cd.markForCheck();
          return of<AsyncFetchResult<V, M>>({
            items: [],
            total: this.state.total,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(result => {
        const seen = new Set(
          this.state.items.map(item => this.trackByValue(0, item)),
        );
        const appended = result.items.filter(
          item => !seen.has(this.trackByValue(0, item)),
        );
        this.state = {
          items: [...this.state.items, ...appended],
          total: result.total,
          loading: false,
          loadingMore: false,
          error: this.state.error,
        };
        if (!this.disabled) {
          this.open = true;
        }
        this.cd.markForCheck();
      });
  }

  private isInputFocused(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    const el = document.getElementById(this.effectiveInputId);
    return el !== null && el === document.activeElement;
  }

  private equals(a: V | null | undefined, b: V | null | undefined): boolean {
    if (a === b) return true;
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      const aId = (a as {id?: unknown}).id;
      const bId = (b as {id?: unknown}).id;
      if (aId !== undefined && bId !== undefined) return aId === bId;
    }
    return false;
  }
}
